import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { useGraderActions, useGraderState } from '../../context/GraderContext';

// Keeps the selected grader and the `:judgeId` URL segment in sync in both
// directions without the two ever fighting each other.
//
// Selection lives in reducer state; the URL is a componentless child route
// (`:judgeId`) that re-matches without remounting GraderPage. Because both
// sides can change independently, a naive "mirror selection into the URL"
// effect would, on a browser back/forward or link navigation, see the stale
// selection and immediately push the URL back to it — reverting the user's
// navigation.
//
// To avoid that, we detect which side moved on each render by comparing the
// current route param against the previous one:
//   * route changed  -> the user navigated; the URL is the source of truth, so
//                        adopt it into the selection (route -> state).
//   * route unchanged -> the change came from in-app selection; mirror it into
//                        the URL (state -> URL).
export function useGraderRouteSync(workspaceName: string): void {
  const navigate = useNavigate();
  const { judgeId: routeJudgeId } = useParams<{ judgeId: string }>();
  const { gradersLoading, selectedId } = useGraderState();
  const { selectGrader } = useGraderActions();

  const prevRouteJudgeIdRef = React.useRef<string | undefined>(routeJudgeId);
  const routeChanged = routeJudgeId !== prevRouteJudgeIdRef.current;
  prevRouteJudgeIdRef.current = routeJudgeId;

  // Route -> state: when the URL changes (back/forward/direct link), adopt the
  // route's judge as the selection. Guarded to route changes so it never
  // reverts an in-app selection that hasn't reached the URL yet.
  React.useEffect(() => {
    if (gradersLoading || !routeChanged) return;
    const nextSelectedId = routeJudgeId ?? null;
    if ((selectedId ?? null) !== nextSelectedId) {
      selectGrader(nextSelectedId);
    }
  }, [routeChanged, routeJudgeId, selectedId, gradersLoading, selectGrader]);

  // State -> URL: when in-app selection changes the judge, keep the URL in
  // step. Going through React Router (rather than window.history) keeps the
  // router's location in sync with the address bar. Guarded to skip renders
  // where the route just changed, letting the route -> state branch win instead
  // of navigating back to the old selection.
  React.useEffect(() => {
    if (gradersLoading || routeChanged) return;
    const currentJudgeId = routeJudgeId ?? null;
    const nextJudgeId = selectedId ?? null;
    if (currentJudgeId === nextJudgeId) return;
    const basePath = `/workspaces/${encodeURIComponent(workspaceName)}/graders`;
    const newPath = nextJudgeId
      ? `${basePath}/${encodeURIComponent(nextJudgeId)}`
      : basePath;
    void navigate(newPath, { replace: true });
  }, [
    routeChanged,
    selectedId,
    workspaceName,
    gradersLoading,
    routeJudgeId,
    navigate,
  ]);
}
